import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Delningsbild: riktigt foto från salongen på Åhléns City (1200x630)
import ogImg from "../assets/og-salong.jpg";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { REVIEW_COUNT_TEXT } from "@/lib/booking";
import { loadAnalytics } from "@/lib/load-analytics";
import { initConsent } from "@/lib/consent";
import { ConsentBanner } from "@/components/ConsentBanner";
import { initOutboundTracking } from "@/lib/track-outbound";
import { initPageViewTracking } from "@/lib/page-view";
import { organizationSchema, jsonLd, SITE_URL } from "@/lib/seo";

// Google Consent Mode v2 defaults. Must execute before gtag.js loads so that
// nothing is stored until our own consent banner updates consent.
const CONSENT_DEFAULTS = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
  wait_for_update: 500
});
gtag('set', 'ads_data_redaction', true);
gtag('set', 'url_passthrough', true);`;


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Sidan kunde inte hittas</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sidan du letar efter finns inte eller har flyttats.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-primary">
            Tillbaka till startsidan
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Något gick fel</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Försök ladda om sidan eller gå tillbaka till startsidan.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="btn-primary"
          >
            Försök igen
          </button>
          <a href="/" className="btn-outline">
            Till startsidan
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Tailor's Barber — Stockholms ledande barberkedja" },
      {
        name: "description",
        content: `Tailor's Barber — fyra salonger i Stockholm med ${REVIEW_COUNT_TEXT}. Boka herrklippning, fade, skäggtrim och drop-in online via BokaDirekt.`,
      },
      { property: "og:title", content: "Tailor's Barber — Stockholms ledande barberkedja" },
      { property: "og:description", content: `Fyra salonger i Stockholm. ${REVIEW_COUNT_TEXT}. Boka online via BokaDirekt.` },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Tailor's Barber" },
      { property: "og:image", content: `${SITE_URL}${ogImg}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "sv_SE" },
      { name: "twitter:image", content: `${SITE_URL}${ogImg}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#f7f1e6" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Primary favicon derived from the uploaded Tailor's Barber badge logo.
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "alternate", type: "text/plain", href: "https://tailorsbarber.se/llms.txt" },

      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/fraunces-400-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/inter-400-latin.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      // 1. Consent Mode v2 defaults must run before any tag reads them.
      { children: CONSENT_DEFAULTS },
      // 2. Inert JSON-LD, kept after the defaults.
      jsonLd(organizationSchema()),
    ],


  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const language = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "sv";
  return (
    <html lang={language}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  useEffect(() => {
    // Sparat samtycke speglas till Consent Mode innan gtag.js laddas.
    initConsent();
    loadAnalytics();
    // Safe before consent: only pushes to dataLayer, Consent Mode gates storage.
    initOutboundTracking();
    // SPA navigations must send their own page_view; gtag config only sends one.
    initPageViewTracking(router);
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <ConsentBanner />
    </QueryClientProvider>
  );
}
